import React, { useMemo, useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useSnackbar } from 'notistack'
import { isPossiblePhoneNumber } from 'react-phone-number-input'
import _ from 'lodash'

import PatientInfo, { PatientInfoProps } from '@/pages/diagnostic/components/PatientInfo'
import { ConsultationInfo } from './OfflineConsultationInfo'
import { patientOfflineStore, OfflinePatient, OfflineConsultation } from '@/core/offline'
import { parseIntOrUndefined, transformObject } from '@/utils/helpers'
import { Gender } from '@/core/types/shared'

type OfflinePatientInfoProps = {
  consultation: OfflineConsultation
  patient: OfflinePatient
  onPatientUpdate: (patient: OfflinePatient) => void
}

const OfflinePatientInfo = React.memo(function OfflinePatientInfo(props: OfflinePatientInfoProps) {
  const { consultation, patient, onPatientUpdate } = props
  const { t } = useTranslation()
  const { enqueueSnackbar } = useSnackbar()

  const [isUpdating, setIsUpdating] = useState(false)

  const patientInfos = useMemo(() => {
    return _.merge({
      name: patient?.userInfo?.name || '',
      surname: patient?.userInfo?.surname || '',
      email: patient?.email || '',
      gender: patient?.userInfo?.gender || '',
      phone:
        !!patient && patient.contactInfo && typeof patient.contactInfo.phone === 'string' && isPossiblePhoneNumber(patient.contactInfo.phone)
          ? patient.contactInfo.phone
          : '',
      birthday: !patient?.userInfo?.birthday ? null : new Date(patient.userInfo.birthday),
      secu: patient?.secu ? patient.secu : '',
      caisse: patient?.caisse || '',
      codeRegime: patient?.codeRegime || '',
      codeGestion: patient?.codeGestion || '',
      notes: patient?.notes || '',
      pluginId: patient?.pluginId ?? patient?.netsoinsId ?? '',
      background: patient?.background || '',
      treatment: patient?.treatment || '',
      privateDoctor: patient?.attendingPhysician
        ? {
            firstname: patient.attendingPhysician?.firstname || '',
            lastname: patient.attendingPhysician?.lastname || '',
            email: patient.attendingPhysician?.email || '',
            phone: patient.attendingPhysician?.phone || '',
            city: patient.attendingPhysician?.city || '',
            cp: _.toString(patient.attendingPhysician?.cp || ''),
          }
        : null,
      parents: _.map(patient?.parents, (parent) => ({
        firstname: parent.firstname,
        lastname: parent.lastname,
        email: parent.email || '',
        phone: parent.phone || '',
        city: parent.city || '',
        cp: (parent.cp || '') + '',
      })),
      insured: patient?.insured || '',
      insuredRelationship: patient?.insuredRelationship || '',
      insuranceProvider: patient?.insuranceProvider || '',
      primaryInsurance: patient?.primaryInsurance || '',
      secondaryInsurance: patient?.secondaryInsurance || '',
      primaryPolicy: patient?.primaryPolicy || '',
      secondaryPolicy: patient?.secondaryPolicy || '',
      primaryPharmacy: patient?.primaryPharmacy || '',
    })
  }, [patient])

  const transformData = (values: PatientInfoProps['infos']) => {
    return {
      contactInfo: transformObject({
        phone: values.phone,
      }),
      userInfo: transformObject({
        birthday: values.birthday?.toISOString(),
        gender: values.gender as Gender,
        name: values.name || patient.userInfo.name || '',
        surname: values.surname || patient.userInfo.surname || '',
      }),
      ...transformObject({
        email: values.email,
        secu: values.secu,
        caisse: values.caisse,
        codeRegime: values.codeRegime,
        codeGestion: values.codeGestion,
        notes: values.notes,
        pluginId: values.pluginId,
        netsoinsId: values.pluginId,
        background: values.background,
        treatment: values.treatment,
      }),
      parents: values.parents?.map((parent) => ({
        ...transformObject(parent),
        cp: parent.cp,
      })),
      attendingPhysician:
        values.privateDoctor && values.privateDoctor.firstname && values.privateDoctor.lastname
          ? {
              ...transformObject(values.privateDoctor),
              cp: values.privateDoctor.cp,
            }
          : null,
    }
  }

  const handleModifyPatient = async (values: PatientInfoProps['infos']) => {
    try {
      setIsUpdating(true)

      const transformedData = transformData(values)

      // Merge the updated data with the existing patient, ensuring all required fields are present
      const updatedPatient: OfflinePatient = {
        ...patient,
        ...transformedData,
        userInfo: {
          ...patient.userInfo,
          ...transformedData.userInfo,
          name: transformedData.userInfo?.name || patient.userInfo.name,
          surname: transformedData.userInfo?.surname || patient.userInfo.surname,
        },
        parents:
          transformedData.parents?.map((parent, index) => ({
            _id: patient.parents[index]?._id || `parent_${Date.now()}_${index}`,
            firstname: parent.firstname || '',
            lastname: parent.lastname || '',
            email: parent.email,
            phone: parent.phone,
            city: parent.city,
            cp: parent.cp,
            street: patient.parents[index]?.street,
            country: patient.parents[index]?.country,
            patient: patient._id,
          })) || patient.parents,
        attendingPhysician:
          transformedData.attendingPhysician === null
            ? undefined
            : transformedData.attendingPhysician
            ? {
                firstname: transformedData.attendingPhysician.firstname || '',
                lastname: transformedData.attendingPhysician.lastname || '',
                email: transformedData.attendingPhysician.email,
                phone: transformedData.attendingPhysician.phone,
                city: transformedData.attendingPhysician.city,
                cp: transformedData.attendingPhysician.cp,
                street: undefined,
                country: undefined,
              }
            : patient.attendingPhysician,
        _lastModified: Date.now(),
        _syncStatus: 'pending' as const,
      }

      const result = await patientOfflineStore.update(updatedPatient)

      onPatientUpdate(result)
      enqueueSnackbar(t('notification.patient.update.success'), { variant: 'success' })
    } catch (error) {
      console.error('Error updating patient:', error)
      enqueueSnackbar(t('notification.patient.update.error'), { variant: 'error' })
    } finally {
      setIsUpdating(false)
    }
  }

  const handleGetCarteVitale = () => {
    // In offline mode, we can't access carte vitale
    enqueueSnackbar('Carte Vitale not available in offline mode', { variant: 'info' })
  }

  if (!patient) {
    return null
  }

  return (
    <>
      <ConsultationInfo consultation={consultation} />
      <PatientInfo
        infos={patientInfos}
        isModifying={isUpdating}
        onModifyPatient={handleModifyPatient}
        onGetCarteVitale={handleGetCarteVitale}
        readOnly={false}
      />
    </>
  )
})

export default OfflinePatientInfo
